import { Button, Col, Flex, Row } from "antd";
import DashboardLayout from "../../../layouts/DashboardLayout";
import CustomTitle from "../../../components/CustomTitle";
import ArrowLeftIcon from "../../../assets/icons/ArrowLeftIcon";
import { Link, useNavigate, useParams } from "react-router";
import StateCard from "../../../components/StateCard";
import RoundUserIcon from "../../../assets/icons/RoundUserIcon";
import UsersIcon from "../../../assets/icons/UsersIcon";
import CorrectIcon from "../../../assets/icons/CorrectIcon";
import useFilters from "../../../hooks/useFilters";
import StatusTag from "../../../components/StatusTag";
import type { ColumnsType } from "antd/es/table";
import CustomTable from "../../../components/CustomTable";
import { countParPageSerialNumber } from "../../../utils/global.utils";

const statusConfig = [
  {
    value: "passed",
    label: "Passed",
    className: "bg-green-100 text-green-600",
  },
  {
    value: "failed",
    label: "Failed",
    className: "bg-red-100 text-red-600",
  },
];

export default function QuizDetails() {
  const navigate = useNavigate();
  const { limit, page, handleLimit, handlePagination } = useFilters();
  const { id } = useParams();
  const quizId = id;

  const columns: ColumnsType<LearnerData> = [
    {
      title: "#SL",
      dataIndex: "index",
      align: "center",
      width: 50,
      render: (_value: unknown, _record: LearnerData, index: number) =>
        countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Learner Name",
      dataIndex: "learnerName",
      width: 300,
      render: (val) => <span>{val}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      width: 300,
      render: (val) => <span>{val}</span>,
    },
    {
      title: "Score",
      dataIndex: "score",
      width: 300,
      render: (val) => <span>{val}</span>,
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => <StatusTag value={val} config={statusConfig} />,
    },
    {
      title: "Attempt Date",
      dataIndex: "attemptDate",
      width: 300,
      render: (val) => <span>{val}</span>,
    },
    {
      title: "Time Spent",
      dataIndex: "timeSpent",
      width: 300,
      render: (val) => <span>{val} mins</span>,
    },
    {
      title: "Action",
      dataIndex: "id",
      key: "action",
      render: (id: string) => (
        <Link to={`/admin/quiz/${quizId}/${id}`}>
          <Button size="small" className="btn-icon">
            Details
          </Button>
        </Link>
      ),
    },
  ];

  return (
    <DashboardLayout title="Quiz Details">
      <Flex justify="start" align="center" className="mb-4" gap={16}>
        <Button
          type="default"
          size="middle"
          className="btn-bordered-primary border
               border-zinc-200 hover:!border-zinc-200 focus:!border-zinc-200 active:!border-zinc-200"
          onClick={() => navigate(`/admin/quiz`)}
        >
          <ArrowLeftIcon /> Back
        </Button>
        <CustomTitle
          title="JavaScript Fundamentals Quiz"
          description="Complete Web Development Bootcamp"
        />
      </Flex>

      <Row gutter={[16, 16]} className="mb-5">
        <Col span={6}>
          <StateCard
            icon={<UsersIcon className="text-[#2497EB] text-2xl" />}
            title="Total Attempts"
            count={`540`}
            iconClass="bg-sky-100"
          />
        </Col>
        <Col span={6}>
          <StateCard
            icon={<RoundUserIcon className="text-[#4C56BB] text-2xl" />}
            title="Average Score"
            count={`78%`}
            iconClass="bg-indigo-100"
          />
        </Col>
        <Col span={6}>
          <StateCard
            icon={<RoundUserIcon className="text-[#4C56BB] text-2xl" />}
            title="Pass Rate"
            count={`78%`}
            iconClass="bg-indigo-100"
          />
        </Col>
        <Col span={6}>
          <StateCard
            icon={<CorrectIcon className="text-[#0A9952] text-2xl" />}
            title="Pass Score"
            count={`70%`}
            iconClass="bg-green-100"
          />
        </Col>
      </Row>

      <CustomTable
        tableTitle="Learners Attempts"
        columns={columns}
        dataSource={learnerData}
        limit={limit}
        page={page}
        total={10}
        onLimitChange={handleLimit}
        onPaginate={handlePagination}
        hideRowSelect
        filterSection={<></>}
      />
    </DashboardLayout>
  );
}

interface LearnerData {
  id: number; // changed to number
  learnerName: string;
  email: string;
  score: number;
  status: "Passed" | "Failed";
  attemptDate: string;
  timeSpent: number; // percentage
}

const learnerData: LearnerData[] = [
  {
    id: 1,
    learnerName: "Alice Johnson",
    email: "alice.johnson@example.com",
    score: 85,
    status: "Passed",
    attemptDate: "2026-01-12",
    timeSpent: 75,
  },
  {
    id: 2,
    learnerName: "Bob Smith",
    email: "bob.smith@example.com",
    score: 62,
    status: "Failed",
    attemptDate: "2026-01-11",
    timeSpent: 60,
  },
  {
    id: 3,
    learnerName: "Carol Williams",
    email: "carol.williams@example.com",
    score: 90,
    status: "Passed",
    attemptDate: "2026-01-10",
    timeSpent: 80,
  },
  {
    id: 4,
    learnerName: "David Brown",
    email: "david.brown@example.com",
    score: 70,
    status: "Passed",
    attemptDate: "2026-01-09",
    timeSpent: 50,
  },
  {
    id: 5,
    learnerName: "Eva Green",
    email: "eva.green@example.com",
    score: 95,
    status: "Passed",
    attemptDate: "2026-01-08",
    timeSpent: 90,
  },
  {
    id: 6,
    learnerName: "Frank Taylor",
    email: "frank.taylor@example.com",
    score: 55,
    status: "Failed",
    attemptDate: "2026-01-07",
    timeSpent: 40,
  },
  {
    id: 7,
    learnerName: "Grace Lee",
    email: "grace.lee@example.com",
    score: 78,
    status: "Passed",
    attemptDate: "2026-01-06",
    timeSpent: 65,
  },
  {
    id: 8,
    learnerName: "Henry Miller",
    email: "henry.miller@example.com",
    score: 82,
    status: "Passed",
    attemptDate: "2026-01-05",
    timeSpent: 70,
  },
  {
    id: 9,
    learnerName: "Isla Moore",
    email: "isla.moore@example.com",
    score: 68,
    status: "Passed",
    attemptDate: "2026-01-04",
    timeSpent: 55,
  },
  {
    id: 10,
    learnerName: "Jack Wilson",
    email: "jack.wilson@example.com",
    score: 45,
    status: "Failed",
    attemptDate: "2026-01-03",
    timeSpent: 30,
  },
];
